"use client";

import { ProgressiveImage } from "@/component/ProgressiveImage";
import { VideoPlayer, type IVideoPlayerRef } from "@/component/VideoPlayer";
import { Image as AntdImage, ImageProps as AntdImageProps } from "antd";
import classNames from "classnames";
import Image from "next/image";
import { HTMLAttributes, memo, useEffect, useRef, useState } from "react";
import styles from "./index.module.css";

export interface BlurBackgroundContainerProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  mediaType: "image" | "video";
  hoverScale?: boolean;
  src: string;
  previewSrc: string;
  height?: string | number;
  showControls?: boolean;
  showPlayButton?: boolean;
  alt?: string;
  className?: string;
  backgroundScale?: number;
  onError?: () => void;
  /** 是否启用图片预览功能（仅对图片有效），默认为 false */
  enablePreview?: boolean;
  /** antd Image 预览配置，enablePreview 为 true 时生效 */
  previewConfig?: AntdImageProps["preview"];
  playButtonStyle?: {
    container: string;
    icon: string;
  };
  videoPlayerRef?: React.RefObject<IVideoPlayerRef | null>;
  enableClipRange?: boolean;
  clipRange?: { startTime: number; duration: number };
  onClipRangeChange?: (startTime: number) => void;
  stopSignal?: number;
}

const BlurBackgroundContainerComponent = ({
  mediaType,
  src,
  previewSrc,
  height,
  hoverScale = false,
  showControls = true,
  showPlayButton = true,
  alt = "media",
  className,
  backgroundScale = 1.5,
  onError,
  enablePreview = false,
  previewConfig,
  playButtonStyle,
  videoPlayerRef,
  enableClipRange,
  clipRange,
  onClipRangeChange,
  stopSignal,
  ...props
}: BlurBackgroundContainerProps) => {
  const isVideo = mediaType === "video";
  const baseSrc = src ?? "";
  const hasPreviewSrc = Boolean(previewSrc);
  const hasSrc = Boolean(baseSrc);

  // 懒加载：使用 Intersection Observer 检测组件是否在可视区
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !hasSrc) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            // 一旦可见就停止观察，避免重复触发
            observer.unobserve(entry.target);
          }
        });
      },
      {
        // 提前加载：当元素距离视口还有 50px 时就开始加载
        rootMargin: "50px",
        threshold: 0.01,
      },
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [hasSrc]);

  // 如果没有 src，显示占位状态
  if (!hasSrc) {
    return (
      <div
        ref={containerRef}
        className={classNames(
          "relative flex items-center justify-center overflow-hidden bg-background-elevated",
          className,
        )}
        style={{ height }}
        {...props}
      >
        <Image
          src="/dark-grey-logo.png"
          alt="placeholder"
          width={80}
          height={80}
          className="relative z-[1] object-contain opacity-30"
          draggable={false}
          unoptimized
        />
      </div>
    );
  }

  // TypeScript 类型断言：在 hasSrc 检查之后，src 一定不是 undefined
  const safeSrc = baseSrc as string;

  return (
    <div
      ref={containerRef}
      key={safeSrc}
      className={classNames(
        "relative flex items-center justify-center overflow-hidden",
        className,
      )}
      style={{ height }}
      {...props}
    >
      {/* 只有在可见时才加载资源 */}
      {isVisible ? (
        <>
          {/* 背景模糊层 */}
          <div
            className={classNames(
              "absolute inset-0 h-full w-full bg-cover bg-center bg-no-repeat transition-opacity duration-300",
            )}
            style={{
              backgroundImage: hasPreviewSrc
                ? `url(${previewSrc})`
                : isVideo
                  ? ""
                  : `url(${safeSrc})`,
              filter: `blur(10px)`,
              transform: `scale(${backgroundScale})`,
            }}
          ></div>

          {/* 前景内容层 */}
          {isVideo ? (
            <VideoPlayer
              src={safeSrc}
              ref={videoPlayerRef}
              className={styles.videoPlayer}
              showControls={showControls}
              showPlayButton={showPlayButton}
              playButtonStyle={playButtonStyle}
              enableClipRange={enableClipRange}
              clipRange={clipRange}
              onClipRangeChange={onClipRangeChange}
              stopSignal={stopSignal}
            />
          ) : (
            <div className="absolute inset-0 z-[1] flex h-full w-full bg-black/10 justify-center">
              {enablePreview ? (
                <AntdImage
                  key={safeSrc}
                  src={safeSrc}
                  alt={alt}
                  className={classNames("!h-full !w-full object-contain", {
                    "hover:transform hover:scale-125 transition-transform duration-300":
                      hoverScale,
                  })}
                  preview={previewConfig ?? { mask: null }}
                  draggable={false}
                  onError={onError}
                />
              ) : (
                <ProgressiveImage
                  key={safeSrc}
                  src={safeSrc}
                  alt={alt}
                  fill
                  containerClassName="flex-1"
                  className={classNames("object-contain", {
                    "hover:transform hover:scale-125 transition-transform duration-300":
                      hoverScale,
                  })}
                  skeletonClassName="z-[2]"
                  skeletonTestId="blur-background-skeleton"
                  draggable={false}
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              )}
            </div>
          )}
        </>
      ) : (
        // 未可见时显示占位符
        <Image
          src="/dark-grey-logo.png"
          alt="placeholder"
          width={80}
          height={80}
          className="relative z-[1] object-contain opacity-30"
          draggable={false}
          unoptimized
        />
      )}
    </div>
  );
};

export const BlurBackgroundContainer = memo(BlurBackgroundContainerComponent);
BlurBackgroundContainer.displayName = "BlurBackgroundContainer";
